import { RouteFactory } from "@app/routePaths";
import { Name, Type } from "@app/types/enums";
import ImagePlaceholder from "@components/ArticleCard/components/ImagePlaceholder";
import CreateTime from "@components/ArticleMeta/CreateTime";
import SectionLink from "@components/ArticleMeta/SectionLink";
import SubscriberFlag from "@components/ArticleMeta/SubscriberFlag";
import Summary from "@components/ArticleMeta/Summary";
import TitleLink from "@components/ArticleMeta/TitleLink";
import UpdateTime from "@components/ArticleMeta/UpdateTime";
import ArticleThumbnail from "@components/ArticleThumbnail/ArticleThumbnail";
import BylineForCards from "@components/Byline/BylineForCards";
import StoryThreadLink from "@components/StoryThread/StoryThreadLink";
import { faCircle, faPlay } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IS_PLAYWRIGHT } from "@util/constant";
import { cn, getDefaultImage, getFormattedRedirectionUrl } from "@util/helpers";

import { BasicCardProps } from "./types";

/** @deprecated This component will not be updated. Please use the Card component instead. */
export default function BasicCard({
  rootClassName = "",
  id,
  media,
  kicker = "",
  section,
  title = "",
  paywall = false,
  blurb = "",
  slug = "",
  edited = "",
  updated = "",
  hasUpdatedTimestampDisplay = false,
  variations,
  author = [],
  defaultImage,
  contributorProfile = [],
  h1Tag,
  isTitleLinkRequired = true,
  displayPlaceholderWhenImageLoading = false,
  storyThread,
  preloadLCPImage = false,
}: BasicCardProps): React.ReactElement {
  const cardWrapperClass = [rootClassName];
  const imageWrapperClass = [];
  const contentWrapperClass = [
    variations?.content?.bgColor,
    variations?.content?.width,
  ];

  const _media = media && "length" in media ? media?.[0] : media;

  const mediaContent =
    _media &&
    (_media.content?.type === "picture" ||
      _media.content?.type === Type.ArticleBrightcoveVideo ||
      _media.content?.type === Type.ArticleYoutubeVideo)
      ? _media.content
      : undefined;

  const isVideo =
    _media &&
    (_media.content?.type === Type.ArticleBrightcoveVideo ||
      _media.content?.type === Type.ArticleYoutubeVideo);

  const credit =
    _media?.summary?.find(({ key }) => {
      return key === Name.Credit;
    })?.value || mediaContent?.fields?.credit;
  const caption =
    _media?.summary?.find(({ key }) => {
      return key === Name.Caption;
    })?.value || mediaContent?.fields?.caption;
  const alt =
    _media?.summary?.find(({ key }) => {
      return key === Name.Alt;
    })?.value || title;

  const thumbnail =
    _media && mediaContent
      ? mediaContent
        ? { ...mediaContent, alt: alt, credit: credit, caption: caption }
        : { ..._media, alt: alt, credit: credit, caption: caption }
      : undefined;

  const defaultThumbnail = defaultImage
    ? {
        url: getDefaultImage(defaultImage?.directoryName),
        alt: title,
      }
    : undefined;

  const cardThumbnail = thumbnail || defaultThumbnail;

  switch (variations?.image?.position) {
    case "left":
      imageWrapperClass.push("order-1 flex-shrink-0");
      contentWrapperClass.push("order-2");
      break;

    case "right":
      imageWrapperClass.push("order-2 flex-shrink-0");
      contentWrapperClass.push("order-1");
      break;
  }

  const articleLink = slug
    ? getFormattedRedirectionUrl(slug)
    : RouteFactory.article(id);

  return (
    <div
      data-testid="basic-card"
      data-cueid={id}
      className={cn("relative flex flex-wrap items-start", cardWrapperClass)}
    >
      {!cardThumbnail && displayPlaceholderWhenImageLoading ? (
        <div className="hidden aspect-3x2 w-full bg-gray-850 lg:block"></div>
      ) : null}

      {cardThumbnail ? (
        <div
          className={cn(
            "relative flex",
            imageWrapperClass,
            variations?.image?.extraClass,
            variations?.image?.width
          )}
        >
          {!IS_PLAYWRIGHT ? (
            <>
              <ArticleThumbnail
                loading={variations?.image?.loading}
                thumbnail={cardThumbnail}
                orientation={variations?.image?.orientation || "landscape"}
                imgRootClassName={cn(
                  "w-full",
                  variations?.image?.aspectRatio || "aspect-3x2"
                )}
                filters={variations?.image?.filters}
                rootClassName="flex w-full"
                invisibleImgRootClass={cn(
                  "w-full",
                  variations?.image?.invisibleImgRootClass
                )}
                articleLink={articleLink}
                preloadLCPImage={preloadLCPImage}
              />

              {variations?.image?.isVideo || isVideo ? (
                <div
                  className={
                    variations?.image?.playIconPosition === "lower-left"
                      ? "fa-stack absolute bottom-3 left-2"
                      : ""
                  }
                >
                  <FontAwesomeIcon
                    icon={faCircle}
                    inverse
                    className="fa-stack-2x"
                  />
                  <FontAwesomeIcon icon={faPlay} className="fa-stack-1x" />
                </div>
              ) : null}
            </>
          ) : (
            <ImagePlaceholder aspectRatio={variations?.image?.aspectRatio} />
          )}
        </div>
      ) : null}

      <div
        className={cn(
          "flex flex-grow flex-col",
          contentWrapperClass,
          variations?.content?.extraClass,
          {
            "p-4 text-white": variations?.content?.bgColor,
          }
        )}
      >
        {storyThread ? (
          <StoryThreadLink storyThread={storyThread} className="mb-[.12rem]" />
        ) : null}

        {section ? (
          <SectionLink
            sectionName={section.name}
            sectionUniqueName={section.uniqueName}
            rootClassName={variations?.section?.extraClass}
          />
        ) : null}

        {kicker ? (
          <div
            data-testid="basic-card-kicker-component"
            className={cn(
              "break-words font-poppins text-4xs font-light uppercase leading-4",
              variations?.kicker?.color,
              variations?.kicker?.extraClass,
              {
                "text-white": variations?.content?.bgColor,
              }
            )}
          >
            {kicker}
          </div>
        ) : null}

        <TitleLink
          title={title}
          articleId={id}
          slug={slug}
          rootClassName={cn(
            variations?.title?.size,
            variations?.title?.extraClass
          )}
          h1Tag={h1Tag}
          linkRootClassName={variations?.title?.color}
          isTitleLinkRequired={isTitleLinkRequired}
        />

        {paywall ? (
          <SubscriberFlag
            rootClassName={variations?.subscriberLabel?.extraClass}
          />
        ) : null}

        {blurb ? (
          <Summary
            rootClassName={cn(
              "font-public-sans text-xs font-normal",
              variations?.content?.blurb?.extraClass,
              { "text-white": variations?.content?.bgColor }
            )}
            summary={blurb}
          />
        ) : null}

        {updated || (edited && hasUpdatedTimestampDisplay) ? (
          <div className="mt-auto">
            {updated ? (
              <CreateTime
                created={updated}
                rootClassName={cn(variations?.content?.updatedTimeExtraClass)}
              />
            ) : null}

            {edited && hasUpdatedTimestampDisplay ? (
              <UpdateTime
                updated={updated}
                edited={edited}
                rootClassName={cn(
                  "text-red",
                  variations?.content?.updatedTimeColor
                )}
              />
            ) : null}
          </div>
        ) : null}

        {contributorProfile.length > 0 || author.length > 0 ? (
          <BylineForCards
            authors={author}
            contributors={contributorProfile}
            imageWidth={variations?.byline?.imageWidth}
            imageHeight={variations?.byline?.imageHeight}
            size={variations?.byline?.size}
            rootClassName={cn("mt-2", variations?.byline?.extraClass)}
          />
        ) : null}
      </div>
    </div>
  );
}
